import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useT } from "../i18n/LanguageContext";

const LangToggle = ({ light }) => {
  const { lang, setLang } = useT();
  return (
    <div
      data-testid="lang-toggle"
      role="group"
      aria-label="Language"
      className={`hidden md:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.24em] font-medium border select-none ${
        light ? "border-[#EFECE6]/60" : "border-[#4c535a]/40"
      }`}
    >
      {["en", "de"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1.5 transition-colors duration-300 ${
            lang === l
              ? light
                ? "bg-[#EFECE6] text-[#4c535a]"
                : "bg-[#4c535a] text-[#EFECE6]"
              : light
              ? "text-[#EFECE6] hover:bg-[#EFECE6]/10"
              : "text-[#4c535a] hover:bg-[#4c535a]/10"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useT();
  const location = useLocation();
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const overVideo = location.pathname === "/" && !scrolled;

  const NAV = [
    { to: "/", label: t.nav.home, key: "home" },
    { to: "/method", label: t.nav.method, key: "method" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/studio", label: t.nav.studio, key: "studio" },
    { to: "/faq", label: t.nav.faq, key: "faq" },
  ];

  const logoSrc = overVideo ? "/logo-white.png" : "/logo-olive.png";

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background,border,color] duration-500 ${
        scrolled
          ? "py-3 bg-[#EFECE6]/85 backdrop-blur-xl border-b border-[#4c535a]/15"
          : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4 relative">
        {/* Logo (image + wordmark on one line, tagline below) */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group flex-none">
          <img
            src={logoSrc}
            alt="Ordo Rex Studio"
            className={`w-auto transition-all duration-500 ${scrolled ? "h-7 md:h-8" : "h-9 md:h-10"}`}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.22em] font-medium link-sweep transition-colors duration-500 ${
                  overVideo
                    ? isActive
                      ? "text-[#EFECE6]"
                      : "text-[#EFECE6]/80"
                    : isActive
                    ? "text-[#4c535a]"
                    : "text-[#4c535a]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LangToggle light={overVideo} />
          <Link
            to="/enquiry"
            data-testid="header-enquire-cta"
            className={`group relative inline-flex items-center gap-2 px-5 py-2.5 border text-[11px] uppercase tracking-[0.22em] font-medium overflow-hidden transition-colors duration-500 ${
              overVideo ? "border-[#EFECE6] text-[#EFECE6]" : "border-[#4c535a] text-[#4c535a]"
            }`}
          >
            <span
              className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                overVideo ? "bg-[#EFECE6]" : "bg-[#4c535a]"
              }`}
            />
            <span
              className={`relative z-10 transition-colors duration-500 ${
                overVideo ? "group-hover:text-[#4c535a]" : "group-hover:text-[#EFECE6]"
              }`}
            >
              {t.nav.enquire}
            </span>
          </Link>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
